import { useState } from "react";
import { confirmAction } from "@/components/app/Confirm";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { COMP_LABELS } from "@/lib/fit";

const KEYS = ["resume", "experience", "personality", "work_sample", "interview", "fit"];

export function ScoreOverrideDialog({ appId, overrides, wsEnabled, open, onOpenChange, onChanged }) {
  const [component, setComponent] = useState("interview");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const max = component === "fit" ? 10 : 100;
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  const save = () => {
    if (reason.trim().length < 5) return toast.error("Add a reason (at least 5 characters)");
    const v = Number(value);
    if (value === "" || Number.isNaN(v) || v < 0 || v > max) return toast.error(`Enter a value between 0 and ${max}`);
    run(() => api.post(`/applications/${appId}/score-override`, { component, value: v, reason }), "Override saved. Fit Score recalculated.").then(() => { setReason(""); setValue(""); onOpenChange(false); });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[6px]" data-testid="score-override-dialog">
        <DialogHeader><DialogTitle>Override an AI score</DialogTitle><DialogDescription>Overrides need a reason, show an "overridden" badge and are written to the audit log.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-[1fr_120px] gap-2">
          <select value={component} onChange={(e) => setComponent(e.target.value)} className="h-9 rounded-lg border border-line bg-white px-2 text-sm" data-testid="score-override-component">
            {KEYS.filter((k) => k !== "work_sample" || wsEnabled).map((k) => <option key={k} value={k}>{COMP_LABELS[k]} ({k === "fit" ? "0–10" : "0–100"})</option>)}
          </select>
          <Input type="number" min={0} max={max} step={component === "fit" ? 0.1 : 1} value={value} onChange={(e) => setValue(e.target.value)} placeholder={`0–${max}`} data-testid="score-override-value" />
        </div>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you changing it? (required)" data-testid="score-override-reason" />
        {Object.keys(overrides).length > 0 && (
          <div className="space-y-1 rounded-[5px] bg-subtle p-3 text-xs" data-testid="score-override-list">
            {Object.entries(overrides).map(([k, o]) => (
              <div key={k} className="flex items-center gap-2"><span className="flex-1"><strong>{COMP_LABELS[k]}</strong> {o.original ?? "—"} → {o.value} · {o.by}: {o.reason}</span>
                <Button size="sm" variant="ghost" onClick={async () => (await confirmAction({ title: "Remove this override?", body: "The AI score is used again and the Fit Score is recalculated.", entity: k, confirmLabel: "Remove override" })) && run(() => api.delete(`/applications/${appId}/score-override/${k}`), "Override removed")} data-testid={`score-override-remove-${k}`}>Remove</Button></div>
            ))}
          </div>
        )}
        <DialogFooter><Button onClick={save} data-testid="score-override-save">Save override</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
